import { Search } from "lucide-react"
import {
  type ChangeEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOnline } from "@/hooks/use-online"
import { cn } from "@/lib/utils"

interface ListToolbarProps {
  /**
   * Current search value — driven from URL state by the consuming page.
   * The toolbar maintains a local mirror so typing feels instant; commits
   * to the parent (and thus URL) via the debounced `onSearchChange`.
   */
  searchValue: string
  /**
   * Fires when the debounced search value changes. Caller is responsible
   * for routing (`router.get(currentPath, { ...filters, q }, { ... })`).
   */
  onSearchChange: (value: string) => void
  /**
   * Slot for filter controls (e.g. date range, blocklisted toggle).
   * Rendered to the right of the search input on desktop, below on mobile.
   */
  filters?: ReactNode
  /**
   * Debounce window in ms. Defaults to 300ms per UX Spec §3 (search input pattern).
   */
  debounceMs?: number
  /**
   * Translation key for the search input's placeholder. Defaults to
   * `admin.shared.search_placeholder` ("Search…" / "Rechercher…").
   */
  searchPlaceholderKey?: string
  className?: string
}

/**
 * ListToolbar — search + filter row for the four admin list pages.
 *
 * Owns ONLY the local search-input state + 300ms debounce. Filter state is
 * passed through as a slot — consuming pages render their filter controls
 * (date range, blocklisted toggle) and route through Inertia themselves.
 *
 * When the browser reports offline (`useOnline === false`), the search
 * input is disabled per UX Spec §4 (offline state on list pages).
 *
 * Test IDs:
 *   - `admin-list-search` on the search input (locked v6 testid contract).
 *   - Filter controls inside the `filters` slot expose their own testids.
 */
export function ListToolbar({
  searchValue,
  onSearchChange,
  filters,
  debounceMs = 300,
  searchPlaceholderKey = "admin.shared.search_placeholder",
  className,
}: ListToolbarProps) {
  const { t } = useTranslation()
  const online = useOnline()

  // Local mirror of the URL-driven search value. Re-syncs whenever the
  // upstream `searchValue` prop changes (e.g. user navigates back to a
  // different filter set, or `[Clear filters]` zeroes the query).
  const [local, setLocal] = useState(searchValue)
  useEffect(() => {
    setLocal(searchValue)
  }, [searchValue])

  // Debounce the commit upward. Skip the initial mount when the local
  // value matches the upstream value (no-op fire on first render).
  const skipNextCommit = useRef(true)
  useEffect(() => {
    if (skipNextCommit.current) {
      skipNextCommit.current = false
      return
    }
    if (local === searchValue) return
    const timer = window.setTimeout(() => {
      onSearchChange(local)
    }, debounceMs)
    return () => window.clearTimeout(timer)
    // searchValue intentionally omitted — the local-vs-upstream gate above
    // handles the prop-change case; including it would re-fire timers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local, debounceMs, onSearchChange])

  function handleInput(e: ChangeEvent<HTMLInputElement>) {
    setLocal(e.target.value)
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="relative w-full sm:max-w-sm">
        <Label htmlFor="admin-list-search" className="sr-only">
          {t(searchPlaceholderKey)}
        </Label>
        <Search
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        />
        <Input
          id="admin-list-search"
          type="search"
          inputMode="search"
          value={local}
          onChange={handleInput}
          placeholder={t(searchPlaceholderKey)}
          disabled={!online}
          aria-disabled={!online ? "true" : undefined}
          className="pl-9"
          data-testid="admin-list-search"
        />
      </div>
      {filters && (
        <div className="flex flex-wrap items-center gap-2">{filters}</div>
      )}
    </div>
  )
}
